import { useEffect, useRef, useState } from 'react';
import { useTranslate } from '../../application/i18n/LocaleProvider';
import useRevealSequence from '../../application/motion/useRevealSequence';
import { carName } from '../../domain/cars/carCatalogue';
import { selectTake } from '../../domain/cars/carSchema';
import { artSource } from '../cars/carArt';
import { BrandBadge, BrandWordmark } from '../chrome/BrandMark';
import CarPrizeCard from './CarPrizeCard';

/**
 * A car being won, as a ceremony (SPEC-010 PR-10.4).
 *
 * The whole of this component is one argument: a card that arrives in a list is
 * not a card anybody remembers arriving. The catalogue's two dozen cut-outs are
 * the most expensive thing in the product and they were all spent in the first
 * thirty seconds of scrolling — a picture a reader has EARNED is looked at for a
 * minute, and the same picture handed over in a grid is looked at for a second
 * and a half. So the car is withheld for two and a half seconds and then given a
 * walkout.
 *
 * FIVE ACTS, and `useRevealSequence` owns their clock. What this file owns is
 * what is on screen during each — which is deliberately a MOUNT per act rather
 * than a class swap on a persistent tree, because a CSS entrance only plays for
 * an element that is new and this sheet is nothing but entrances.
 *
 * The three that persist across acts are the exceptions, and each is one:
 *
 *   the FLARE   arrives with the rarity and stays under the car, because it is
 *               the light the car is standing in rather than a transition.
 *   the ART     arrives on the walkout and is still there on the card. Remounting
 *               it would refetch a 1600px picture at the exact moment the reader
 *               is looking at it.
 *   the NAME    the same, and for the same reason: it slams in once.
 *
 * A real `<dialog>` opened with `showModal()` — the third in the app after
 * `CarShareSheet` and `AccountSheet` — so focus trapping, Escape, the top layer
 * and an inert background come from the platform. Escape is INTERCEPTED and
 * skips to the card rather than closing (SPEC-010 D-12): a reader who dismissed
 * a car they had just won would have no way back into the moment, and the
 * ceremony would have taken the prize away.
 */

/**
 * Does this reader want the ceremony at all?
 *
 * The fourth hand-rolled copy of this read in the app, taking `matchMedia` as a
 * defaulted parameter per the repo's rule for browser APIs — and here that rule
 * pays for itself twice, because both branches of a two-and-a-half-second
 * animation have to be assertable without waiting for either.
 *
 * A browser without `matchMedia` answers false and gets the sequence, which is
 * the safe way round: a reader who asked for less motion is always detectable,
 * and one who did not ask for anything has expressed no preference.
 */
export function prefersReducedMotion(matchMedia = typeof window === 'undefined' ? null : window.matchMedia) {
    try {
        return Boolean(matchMedia?.call(window, '(prefers-reduced-motion: reduce)')?.matches);
    } catch (error) {
        return false;
    }
}

/**
 * One half of the card back.
 *
 * Both halves carry the WHOLE face and each clips away the other side of it, so
 * the two are one drawing seen through two windows. The alternative — a wordmark
 * in one half and a badge in the other — comes apart into two objects that were
 * never a single card, which is the opposite of a tear.
 *
 * Carbon (§09) rather than a plate: a sealed pack is a MATERIAL, and the one the
 * brand's frames are drawn on is the material it should be made of.
 */
function PackFace({ side }) {
    return (
        <span className={`car-prize__half car-prize__half--${side}`} aria-hidden="true">
            <span className="car-prize__face">
                <BrandWordmark className="car-prize__wordmark" sizes="180px" />
                <BrandBadge className="car-prize__badge" sizes="64px" />
            </span>
        </span>
    );
}

export default function CarPrizeReveal({
    car,
    closeKey = 'prize.close',
    nextTarget = null,
    record = null,
    onClose,
    matchMedia = typeof window === 'undefined' ? null : window.matchMedia,
}) {
    const t = useTranslate();
    const dialogRef = useRef(null);

    // Read ONCE, in a lazy initialiser. Re-reading on a later render would let
    // the ceremony change what it is halfway through — the same trap the splash
    // guards against, and the same fix.
    const [reducedMotion] = useState(() => prefersReducedMotion(matchMedia));

    const {
        isIntroduced,
        open,
        skip,
        stage,
    } = useRevealSequence({ reducedMotion });

    // Opening is a one-off and deliberately its own effect: one that depended on
    // `onClose` would close and re-open the dialog on every parent render, and
    // the second `showModal()` throws.
    useEffect(() => {
        const dialog = dialogRef.current;

        dialog.showModal();

        return () => {
            if (dialog.open) {
                dialog.close();
            }
        };
    }, []);

    const name = carName(car);
    // The WINDOW-DOWN take where the car has one, and the side profile where it
    // does not — `selectTake` owns that fallback, and nine of the twenty-four
    // have the shot today.
    //
    // Act four already drives the car in from the left under its own power. A
    // driver looking out of it is what that walkout was always miming: the car
    // does not simply appear, somebody brings it, and the prize is the keys as
    // much as it is the machine. This is the moment the whole ceremony is built
    // to arrive at, so it gets the picture that is only for arriving.
    //
    // Both takes are cut-outs on transparency, which is what this stage needs.
    // `selectTake` can still fall through as far as the front view, which is
    // NOT — it brings its own lit ground baked in, and a ground is exactly what
    // the flare underneath is already providing. No car in the catalogue reaches
    // that rung today: the second art drop gave every one of the twenty-four a
    // side profile. The ladder is kept because the delivery is incremental and a
    // ceremony is the wrong place to discover a car has no picture.
    const art = artSource(car, selectTake(car));
    const isRevealed = stage === 'walkout' || isIntroduced;

    return (
        <dialog
            /* Two modifiers and they never collide: the stages are `sealed`,
               `tearing`, `rarity`, `walkout` and `introduced`, and the tiers are
               `common`, `rare` and `legendary`. The tier one carries the
               INTENSITY of the flare rather than its colour — see the sheet. */
            className={`car-prize car-prize--${stage} car-prize--${car.rarity}`}
            ref={dialogRef}
            aria-labelledby="car-prize-title"
            onCancel={(event) => {
                // Escape reaches the sequence, not the dialog. Once the card is
                // on screen there is nothing left to skip, so it closes.
                if (!isIntroduced) {
                    event.preventDefault();
                    skip();
                }
            }}
        >
            {/* The field the whole ceremony happens on: five heat streaks at the
                angle the display face leans, and a pool of the tier's own light
                behind whatever is centre stage. Decorative — everything it is
                lighting is announced by its own text. */}
            <div className="car-prize__field" aria-hidden="true">
                <div className="car-prize__streaks">
                    {[0, 1, 2, 3, 4].map((streak) => (
                        <span className="car-prize__streak" key={streak} style={{ '--streak': streak }} />
                    ))}
                </div>
                <span className="car-prize__flare" />
            </div>

            <div className="car-prize__stage">
                {stage === 'sealed' || stage === 'tearing' ? (
                    <button
                        type="button"
                        className="car-prize__pack"
                        /* `aria-disabled` rather than `disabled`, the same way
                           the duel's answered cards are left in place: a
                           `disabled` button drops focus at the moment it is
                           pressed, and inside a modal that leaves the reader
                           nowhere. The guard is the handler's. */
                        aria-disabled={stage === 'tearing'}
                        onClick={stage === 'sealed' ? open : undefined}
                    >
                        {/* ONE face, drawn twice and clipped down the middle.
                            Two different halves would come apart into two
                            objects that were never one card; two clips of the
                            same face come apart into a card that tears. */}
                        <PackFace side="left" />
                        <PackFace side="right" />

                        {/* The ramp under the split, which is what the tear
                            reveals rather than what it is drawn with. */}
                        <span className="car-prize__seam" aria-hidden="true" />

                        {/* Outside the halves, so the instruction does not get
                            torn in two along with the card it is about. */}
                        <span className="car-prize__prompt">
                            <span className="car-prize__prompt-lead">{t('prize.sealed')}</span>
                            <span className="car-prize__prompt-action">{t('prize.open')}</span>
                        </span>
                    </button>
                ) : null}

                {/* The tension beat. The ribbon is the only part of a card that
                    can be shown without giving the car away, which is the whole
                    reason it goes first (SPEC-010 D-10) — and it stays under the
                    walkout, because a rarity that vanished as the car arrived
                    would read as a loading state rather than as a claim. */}
                {stage !== 'sealed' && stage !== 'tearing' ? (
                    <p className={`rarity-ribbon rarity-ribbon--${car.rarity} car-prize__rarity`}>
                        {t(`cars.rarity.${car.rarity}`)}
                    </p>
                ) : null}

                {isRevealed && art ? (
                    <img
                        className="car-prize__art"
                        src={art.src}
                        srcSet={art.srcSet}
                        sizes="(min-width: 640px) 560px, 96vw"
                        /* The one picture in this ceremony that is not
                           decorative: at this moment it IS the announcement, and
                           the heading beside it has not been drawn yet. */
                        alt={t('a11y.carArtwork', { car: name })}
                        /* The whole point of the two seconds before this is that
                           the picture is ready when it lands. */
                        fetchPriority="high"
                        decoding="async"
                    />
                ) : null}

                {isRevealed ? (
                    <h2 className="car-prize__name" id="car-prize-title">
                        {t('prize.won', { car: name })}
                    </h2>
                ) : null}
            </div>

            {/* The heading has to exist from the first frame — a dialog labelled
                by an id nothing has rendered yet is a dialog with no accessible
                name — so before the walkout it is the ceremony's own title, and
                only its pixels are gone. */}
            {isRevealed ? null : (
                <h2 className="visually-hidden" id="car-prize-title">{t('prize.title')}</h2>
            )}

            {/* One visible way past the ceremony, because Escape is a key most
                phones do not have and a wait is not a control. It goes the
                moment there is nothing left to skip. */}
            {isIntroduced ? null : (
                <button type="button" className="button button--quiet car-prize__skip" onClick={skip}>
                    {t('prize.reveal')}
                </button>
            )}

            {isIntroduced ? (
                <CarPrizeCard
                    car={car}
                    closeKey={closeKey}
                    name={name}
                    nextTarget={nextTarget}
                    record={record}
                    onClose={() => {
                        onClose();
                        dialogRef.current?.close();
                    }}
                />
            ) : null}
        </dialog>
    );
}
